import { AxiosResponse } from 'axios'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { $axios } from './axios'
import { EmailType } from '../../server/models/Email'

// Normalize date to midnight for consistent caching
const normalizeToMidnight = (date: Date): Date => {
  const normalized = new Date(date)
  normalized.setHours(0, 0, 0, 0)
  return normalized
}

const getEmails = (
  inboxId: string,
  params: {
        startDate?: Date
        endDate?: Date
        ignore?: boolean
    } = {}
): Promise<AxiosResponse<EmailType[]>> => {
  const { startDate, endDate } = params

  return $axios.get(`/inboxes/${inboxId}/emails`, {
    params: {
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
      ignore: params.ignore
    }
  })
}

export type UseEmailsParams = {
    startDate?: Date
    endDate?: Date
    ignore?: boolean
}

export const useEmailsQuery = (
  inboxId: string,
  params: UseEmailsParams = {},
  options?: Omit<UseQueryOptions<EmailType[], Error>, 'queryKey' | 'queryFn'>
) => {
  // Normalize dates to midnight for consistent cache keys
  const normalizedStartDate = params.startDate ? normalizeToMidnight(params.startDate) : undefined
  const normalizedEndDate = params.endDate ? normalizeToMidnight(params.endDate) : undefined

  return useQuery<EmailType[], Error>({
    queryKey: ['emails', inboxId, normalizedStartDate?.toISOString(), normalizedEndDate?.toISOString(), params.ignore],
    queryFn: async () => {
      const res = await getEmails(inboxId, {
        ...params,
        startDate: normalizedStartDate,
        endDate: normalizedEndDate
      })
      return res.data
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - liberal caching
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for a long time
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options
  })
}

const getEmailsCount = (
  inboxId: string,
  params: {
        startDate?: Date
        endDate?: Date
        ignore?: boolean
    } = {}
): Promise<AxiosResponse<{ count: number }>> => {
  const { startDate, endDate } = params

  return $axios.get(`/inboxes/${inboxId}/emails/count`, {
    params: {
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
      ignore: params.ignore
    }
  })
}

export type UseEmailsCountParams = {
    startDate?: Date
    endDate?: Date
    ignore?: boolean
}

export const useEmailsCountQuery = (
  inboxId: string,
  params: UseEmailsCountParams = {},
  options?: Omit<UseQueryOptions<number, Error>, 'queryKey' | 'queryFn'>
) => {
  // Normalize dates to midnight for consistent cache keys
  const normalizedStartDate = params.startDate ? normalizeToMidnight(params.startDate) : undefined
  const normalizedEndDate = params.endDate ? normalizeToMidnight(params.endDate) : undefined

  return useQuery<number, Error>({
    queryKey: ['emailsCount', inboxId, normalizedStartDate?.toISOString(), normalizedEndDate?.toISOString(), params.ignore],
    queryFn: async () => {
      const res = await getEmailsCount(inboxId, {
        ...params,
        startDate: normalizedStartDate,
        endDate: normalizedEndDate
      })
      return res.data.count
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - liberal caching
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for a long time
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options
  })
}

export const getEmailHtml = (
  inboxId: string,
  emailId: string
): Promise<AxiosResponse<string>> => {
  return $axios.get(`/inboxes/${inboxId}/emails/${emailId}/html`)
}

export const ignoreEmail = (
  inboxId: string,
  emailId: string
): Promise<AxiosResponse<void>> => {
  return $axios.put(`/inboxes/${inboxId}/emails/${emailId}/ignore`)
}
